import React, { useState } from 'react';
import { Poppins, StyledAvatar, TableSkeleton } from 'src/common';
import { useStateSelector } from 'src/redux';
import { getProfileCredentials } from 'src/utils/auth';
import styled from 'styled-components';
import Menu from '@mui/material/Menu';
import AccessTable from './comps/AccessTable';
import { MemberAccess, useFlatOrganisationsUserLazyQuery } from 'src/graphql/_generated';
import { FlatOrgUserData } from 'src/utils/treeNode';
import { SelectedOrgUnit } from '../types';

const Div = styled.div`
  .seletor {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      top: -10px;
      left: -10px;
      right: -10px;
      bottom: -10px;
    }
  }
`;

const StyledMenu = styled(Menu)`
  & .MuiPaper-root {
    border-radius: 6px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.white} !important;
  }

  .org-access-menu {
    padding: 16px 20px;
    min-width: 550px;
    max-width: 1160px;
    max-height: 80vh;
    overflow-y: auto;
    cursor: default;
  }
`;

interface OrgAccessProps {
  targetUserEmail: string | null;
  isOwnProfile: boolean;
  selectedOrgUnit?: SelectedOrgUnit;
  onChange: (selected: SelectedOrgUnit | null) => void;
  closeModal: () => void;
}

export const OrgAccess: React.FC<OrgAccessProps> = ({
  targetUserEmail,
  isOwnProfile,
  selectedOrgUnit,
  onChange,
  closeModal,
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);

  const [loadFlatOrgs, { data: flatOrganisationsData, loading: flatOrgsLoading }] = useFlatOrganisationsUserLazyQuery();

  const flatOrganisations = flatOrganisationsData?.flatOrganisations || [];

  const handleRowSelect = (selected: FlatOrgUserData | null) => {
    onChange(selected);
    handleClose();
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
    e.stopPropagation();
    if (!flatOrganisationsData && targetUserEmail) {
      loadFlatOrgs({ variables: { memberEmail: targetUserEmail } });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  return (
    <Div>
      <div className="seletor" onClick={handleClick}>
        {selectedOrgUnit ? (
          <>
            <StyledAvatar src={''} $gradientBlue={selectedOrgUnit.recordType === 'W'} $xsmall>
              {getProfileCredentials(selectedOrgUnit.name)}
            </StyledAvatar>{' '}
            <Poppins weight={500} px={14} color="prussianBlue">
              {selectedOrgUnit.name}
            </Poppins>
          </>
        ) : (
          <>
            <Poppins weight={500} px={14} color="prussianBlue">
              [GLOBAL]
            </Poppins>
          </>
        )}
        <svg
          style={{ rotate: (isOpen ? 180 : 0) + 'deg' }}
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
        >
          <path
            d="M10.834 0.833007L5.83398 5.83301L0.833984 0.833008"
            stroke={colors.prussianBlue}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <StyledMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: '12px',
            },
          },
        }}
        autoFocus={false}
        disableAutoFocusItem
        anchorEl={anchorEl}
        open={isOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleClose();
          }
        }}
      >
        <div className="org-access-menu">
          {flatOrgsLoading || !flatOrganisationsData ? (
            <div css="max-width: 1120px; width: 1120px;">
              <TableSkeleton rowHeight={32} />
            </div>
          ) : (
            flatOrganisations.length > 0 && (
              <AccessTable
                data={flatOrganisations}
                selectedId={selectedOrgUnit?.id}
                onRowSelect={handleRowSelect}
                isOwnProfile={isOwnProfile}
                hasUnitSelected={!!selectedOrgUnit}
                onViewGlobal={() => {
                  onChange(null);
                  handleClose();
                }}
                closeModal={closeModal}
                isRowDisabled={(item) => {
                  const allowedAccess = [MemberAccess.Allowed];
                  return !allowedAccess.includes(item.member?.access as MemberAccess);
                }}
              />
            )
          )}
        </div>
      </StyledMenu>
    </Div>
  );
};
